import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ClipButton } from './ReportWorkspace';

export default function CampaignPanel() {
  const [rows, setRows] = useState([]),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    input = useRef();
  const load = () =>
    axios
      .get('/api/campaigns')
      .then((r) => setRows(r.data?.data || []))
      .catch(() => setRows([]));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMessage('');
    try {
      const body = new FormData();
      body.append('file', file);
      const r = await axios.post('/api/campaigns/upload', body);
      setMessage(`${r.data.rows_loaded} campaign row(s) loaded.`);
      load();
    } catch (err) {
      const d = err.response?.data;
      setMessage(d?.details?.join(' · ') || d?.error || 'Upload failed.');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };
  const clip = {
    page: 'Analytics · Marketing',
    title: 'Campaign performance',
    subtitle: 'Uploaded plans and matched conversation evidence',
    visual: 'table',
    scope: 'All uploaded campaign plans',
    finding:
      'Actuals count matching recorded conversations within each uploaded campaign scope. They do not measure sales or prove campaign impact.',
    rows: rows
      .slice(0, 7)
      .map((x) => ({
        label: `${x.campaign_name} · ${x.brand}`,
        value: `${x.actual_conversations || 0} / ${x.target_conversations || 0} conversations`,
        status:
          Number(x.target_conversations || 0) > 0 &&
          Number(x.actual_conversations || 0) >= Number(x.target_conversations)
            ? 'green'
            : 'amber',
      })),
  };
  return (
    <div className="campaign-panel">
      <section className="decision-pane campaign-start">
        <header>
          <h3>Campaign plan and results</h3>
          <p>
            Define the intended brand, crop, place, period and targets. FieldForce then tracks matching
            conversation evidence as it arrives.
          </p>
        </header>
        <div className="campaign-actions">
          <a className="campaign-button" href="/api/campaigns/template">
            Download Excel template
          </a>
          <button className="campaign-button primary" onClick={() => input.current?.click()} disabled={busy}>
            {busy ? 'Uploading…' : 'Upload completed template'}
          </button>
          <input ref={input} type="file" accept=".xlsx" hidden onChange={upload} />
        </div>
        {message && <p className="campaign-message">{message}</p>}
        <div className="campaign-schema">
          <b>One row represents</b>
          <span>Campaign × brand × crop × geography × date range</span>
          <small>Repeat the campaign ID when a campaign covers several markets.</small>
        </div>
      </section>
      {rows.length ? (
        <section className="decision-pane decision-wide">
          <header>
            <div>
              <h3>Campaign performance</h3>
              <p>Actuals are matched from field conversations within the uploaded scope.</p>
            </div>
            <ClipButton item={clip} />
          </header>
          <div className="campaign-table">
            <div className="campaign-row campaign-head">
              <span>Campaign</span>
              <span>Scope</span>
              <span>Target</span>
              <span>Actual</span>
              <span>Status</span>
            </div>
            {rows.map((x) => {
              const actual = Number(x.actual_conversations || 0),
                target = Number(x.target_conversations || 0),
                pct = target ? Math.round((actual * 100) / target) : null;
              return (
                <div className="campaign-row" key={x.campaign_row_id}>
                  <span>
                    <b>{x.campaign_name}</b>
                    <small>
                      {x.start_date} to {x.end_date}
                    </small>
                  </span>
                  <span>
                    {x.brand}
                    {x.crop ? ` · ${x.crop}` : ''}
                    <small>{[x.province, x.district].filter(Boolean).join(' · ') || 'National'}</small>
                  </span>
                  <span>
                    {target || '—'}
                    <small>conversations</small>
                  </span>
                  <span>
                    {actual}
                    <small>{x.positive_conversations || 0} positive</small>
                  </span>
                  <span>
                    <i
                      className={pct == null ? 'grey' : pct >= 100 ? 'green' : pct >= 60 ? 'amber' : 'red'}
                    />
                    {pct == null ? 'No target' : `${pct}% of target`}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="decision-pane decision-wide campaign-empty">
          <h3>No campaign plans uploaded</h3>
          <p>
            Download the template, replace the example row, and upload it here. Plans will remain available as
            an institutional record.
          </p>
        </section>
      )}
    </div>
  );
}
